/**
 * One endpoint's status mix, drawn across all six windows.
 *
 * A note on what the x-axis is, because it is not what "time series" usually
 * means. Cloudflare's `httpRequestsAdaptiveGroups` is queried once per window
 * and returns *totals*, not buckets, so there is no per-minute series to plot
 * and inventing one would be fabrication. What the API does return is the same
 * endpoint measured over six nested windows — 30m, 6h, 12h, 24h, 72h, 7d — and
 * reading those left to right is the closest honest thing to a time axis: the
 * left end is recent, the right end is the long-run baseline. A 5xx block that
 * is fat on the left and thin on the right is a problem that started recently,
 * which is the single question this screen exists to answer.
 *
 * The windows are nested, so the bars deliberately default to **shares** rather
 * than counts: 7d contains 24h contains 30m, and an absolute stack would put a
 * 10,000-request week next to a 100-request half hour and render the half hour
 * as a 1px sliver exactly when it is the interesting one. Counts remain one
 * click away for anyone asking "how much", not "what proportion".
 *
 * Two states are drawn differently on purpose, matching `StatusRatioBar`:
 * a window the API did not return (not in cache yet) is a **gap**, and a window
 * whose endpoint has `total === null` is also a gap, because in both cases we
 * measured nothing. A code that a *measured* window simply never saw is a real
 * `0` and stacks as one.
 *
 * Colours come from `statusColors`, resolved to literal values because canvas
 * cannot read CSS variables, so red here is the same red as in the table.
 */
import { Suspense, lazy, useMemo, useState } from 'react'

import { WINDOW_IDS, WINDOW_LABEL_KEYS } from '../api/types'
import type { EndpointSummary, WindowId } from '../api/types'
import { useI18n } from '../i18n'
import { bucketFor, numberFormat, percent, readBucketPalette } from './statusColors'
import type { BucketPalette } from './statusColors'

/**
 * ECharts plus zrender is ~1MB. `vite.config.ts` already splits it into its own
 * chunk; importing it lazily is what actually keeps that chunk off the wire
 * until someone opens a drill-down.
 */
const ReactECharts = lazy(() => import('echarts-for-react'))

export interface ChartSeries {
  name: string
  type: 'bar'
  stack: string
  itemStyle: { color: string }
  /** One entry per window, in `WINDOW_IDS` order. `null` is a gap. */
  data: (number | null)[]
}

export interface ChartOption {
  animation: boolean
  grid: Record<string, unknown>
  legend: Record<string, unknown>
  tooltip: Record<string, unknown>
  xAxis: { type: 'category'; data: WindowId[] } & Record<string, unknown>
  yAxis: Record<string, unknown>
  series: ChartSeries[]
}

export interface BuildOptions {
  /** True: 100%-stacked shares. False: absolute request counts. */
  normalize: boolean
  palette: BucketPalette
}

/** Distinct status codes across every window, ascending, so the stack order is
 *  stable and 2xx always sits at the bottom of the bar. */
function statusCodes(windows: Partial<Record<WindowId, EndpointSummary>>): number[] {
  const codes = new Set<number>()
  for (const summary of Object.values(windows)) {
    if (!summary || summary.total === null) continue
    for (const [code, n] of Object.entries(summary.by_status)) {
      const value = Number(code)
      if (Number.isFinite(value) && n > 0) codes.add(value)
    }
  }
  return [...codes].sort((a, b) => a - b)
}

/**
 * The whole contract of this component, kept pure so it can be tested without
 * a canvas.
 *
 * Nothing in here is copy, which is why it takes no `t`: every string the chart
 * shows is a technical token that reads the same in both languages — series
 * names are status codes, the x-axis is the window ids, the legend echoes the
 * series names, and the tooltip and y-axis formatters emit percentages, grouped
 * numbers or an em dash. The words on this screen all live in the JSX below.
 */
export function buildChartOption(
  windows: Partial<Record<WindowId, EndpointSummary>>,
  { normalize, palette }: BuildOptions,
): ChartOption {
  const codes = statusCodes(windows)

  const series: ChartSeries[] = codes.map((code) => ({
    name: String(code),
    type: 'bar',
    stack: 'status',
    itemStyle: { color: palette[bucketFor(code)] },
    data: WINDOW_IDS.map((id) => {
      const summary = windows[id]
      // Not returned, or returned with no data: we did not measure this. A zero
      // would claim we did.
      if (!summary || summary.total === null) return null
      const count = summary.by_status[String(code)] ?? 0
      if (!normalize) return count
      // A measured window that served nothing has no composition to report.
      return summary.total > 0 ? count / summary.total : null
    }),
  }))

  return {
    animation: false,
    grid: { left: 48, right: 16, top: 28, bottom: 48, containLabel: true },
    legend: { type: 'scroll', bottom: 0, icon: 'roundRect' },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      order: 'seriesDesc',
      // Keep the tooltip inside the chart's own box. ECharts otherwise places
      // it wherever the cursor is, and in a scrollable panel the overflow
      // clipped it — the numbers you hovered for were the part cut off.
      confine: true,
      // In shares mode the raw value is a fraction, and ECharts printed it as
      // `0.8142222222222222`. Percent here, thousands separators in counts
      // mode; the axis and the summary lines below already read that way.
      valueFormatter: (value: unknown) =>
        typeof value !== 'number'
          ? '—'
          : normalize
            ? percent(value)
            : numberFormat.format(value),
    },
    xAxis: {
      type: 'category',
      data: [...WINDOW_IDS],
      axisTick: { alignWithLabel: true },
    },
    yAxis: normalize
      ? {
          type: 'value',
          max: 1,
          min: 0,
          axisLabel: { formatter: (v: number) => `${Math.round(v * 100)}%` },
        }
      : { type: 'value', min: 0 },
    series,
  }
}

export interface StatusTimeSeriesProps {
  /** `EndpointDetailResponse.windows` — only cached windows are present. */
  windows: Partial<Record<WindowId, EndpointSummary>>
  /** Chart height in px. */
  height?: number
}

export function StatusTimeSeries({ windows, height = 340 }: StatusTimeSeriesProps) {
  const { t } = useI18n()
  const [normalize, setNormalize] = useState(true)
  // Read once per mount: the palette only changes with the theme, and the chart
  // remounts with the rest of the drill-down when that happens.
  const palette = useMemo(() => readBucketPalette(), [])
  const option = useMemo(
    () => buildChartOption(windows, { normalize, palette }),
    [windows, normalize, palette],
  )

  if (option.series.length === 0) {
    return (
      <section aria-label={t('dashboard.timeSeries.heading')} className="space-y-2">
        <Heading />
        <p className="rounded-md border border-border bg-hatch px-3 py-6 text-center text-sm text-muted">
          {t('dashboard.timeSeries.empty')}
        </p>
      </section>
    )
  }

  return (
    <section aria-label={t('dashboard.timeSeries.heading')} className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <Heading />
        <div className="flex gap-1" role="group" aria-label={t('dashboard.timeSeries.scaleGroup')}>
          <ScaleButton active={normalize} onClick={() => setNormalize(true)}>
            {t('dashboard.scale.shares')}
          </ScaleButton>
          <ScaleButton active={!normalize} onClick={() => setNormalize(false)}>
            {t('dashboard.scale.counts')}
          </ScaleButton>
        </div>
      </div>

      <div style={{ height }} className="rounded-md border border-border p-1">
        <Suspense fallback={<div className="h-full w-full animate-pulse rounded bg-border" />}>
          <ReactECharts
            option={option}
            notMerge
            style={{ height: '100%', width: '100%' }}
            opts={{ renderer: 'svg' }}
          />
        </Suspense>
      </div>

      {/* A canvas is invisible to a screen reader and to anyone who cannot
          separate the colours, so the same numbers are also written out. */}
      <Readout windows={windows} normalize={normalize} />
    </section>
  )
}

function Heading() {
  const { t } = useI18n()
  return (
    <h3
      className="text-sm font-medium text-fg"
      title={t('dashboard.timeSeries.headingTitle')}
    >
      {t('dashboard.timeSeries.heading')}{' '}
      <span className="font-normal text-muted">{t('dashboard.timeSeries.headingHint')}</span>
    </h3>
  )
}

function ScaleButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded border px-1.5 py-0.5 text-xs ${
        active ? 'border-accent text-accent' : 'border-border text-muted hover:text-fg'
      }`}
    >
      {children}
    </button>
  )
}

/** The chart's data as text, one line per window. */
function Readout({
  windows,
  normalize,
}: {
  windows: Partial<Record<WindowId, EndpointSummary>>
  normalize: boolean
}) {
  const { t } = useI18n()
  return (
    <ul className="space-y-0.5 text-xs text-muted">
      {WINDOW_IDS.map((id) => {
        const summary = windows[id]
        // The two silences stay apart here as everywhere else: "not measured"
        // is a window we never read, "no requests" is a window we read that
        // was quiet. Chinese keeps them apart too — 未测量 vs 0 次请求.
        const parts =
          !summary || summary.total === null
            ? t('dashboard.timeSeries.notMeasured')
            : summary.total === 0
              ? t('dashboard.timeSeries.noRequests')
              : Object.entries(summary.by_status)
                  .filter(([, n]) => n > 0)
                  .sort((a, b) => Number(a[0]) - Number(b[0]))
                  .map(([code, n]) =>
                    normalize
                      ? `${code} ${percent(n / (summary.total as number))}`
                      : `${code} ${numberFormat.format(n)}`,
                  )
                  .join(', ')
        return (
          // The tooltip spells out the id: `WINDOW_LABEL_KEYS` is the api
          // area's table of KEYS, so "7d" hovers as "Last 7 days" or
          // "最近 7 天" depending on the reader.
          <li key={id} title={t(WINDOW_LABEL_KEYS[id])}>
            <span className="num text-fg">{id}</span> — {parts}
          </li>
        )
      })}
    </ul>
  )
}
